import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile, the captcha of "Continue without an account" on the
 * agents' connection screen. Its answer is a token the API checks with
 * Cloudflare before it makes a temporary account; a token is good once, so
 * the widget is reset (`resetKey` changes) after every try.
 *
 * Cloudflare's script is loaded the first time a widget is shown, and only
 * then: nobody who signs in loads it.
 */

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      theme?: 'light' | 'dark' | 'auto';
      appearance?: 'always' | 'execute' | 'interaction-only';
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loading: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load'));
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('Turnstile did not load'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
};

const Turnstile = ({
  siteKey,
  action,
  resetKey,
  onToken,
  onError,
}: {
  siteKey: string;
  action: string;
  /** Changed to ask for a new answer: the last token was used. */
  resetKey: number;
  /** The answer, or null when it expired or failed. */
  onToken: (token: string | null) => void;
  /** The widget could not be shown (the script did not load). */
  onError: () => void;
}) => {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  // The latest callbacks, without rendering the widget again for each.
  const callbacks = useRef({ onToken, onError });
  callbacks.current = { onToken, onError };

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) {
          return;
        }
        widget.current = turnstile.render(container.current, {
          sitekey: siteKey,
          action,
          theme: 'dark',
          // Nothing shown unless Cloudflare needs the person to do something.
          appearance: 'interaction-only',
          callback: (token) => callbacks.current.onToken(token),
          'expired-callback': () => callbacks.current.onToken(null),
          'error-callback': () => callbacks.current.onToken(null),
        });
      })
      .catch(() => {
        if (!cancelled) {
          callbacks.current.onError();
        }
      });
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) {
        window.turnstile.remove(widget.current);
      }
      widget.current = null;
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (resetKey && widget.current && window.turnstile) {
      callbacks.current.onToken(null);
      window.turnstile.reset(widget.current);
    }
  }, [resetKey]);

  return <div ref={container} className="flex justify-center empty:hidden" />;
};

export default Turnstile;
